import {
  createAlphaChannel,
  createQuickMaskChannel,
  findChannel,
  nextChannelName,
  QUICK_MASK_ID,
  quickMaskChannel,
  type MaskViewMode,
} from '../core/channels';
import { applyLayerPatch, findLayerById, isSurfaceLayer, mapLayer } from '../core/layerTree';
import type { AlphaChannel, ChannelRef, DocumentMeta } from '../core/types';
import { getEngine } from '../engine/engine';
import {
  blankSnapshot,
  channelOp,
  layerMaskOp,
  selectionOp,
  type EngineHistoryOp,
} from '../engine/historyOps';
import {
  applyLayerMask as applyLayerMaskPixels,
  channelFromSelection,
  invertLayerMask as invertLayerMaskPixels,
  maskFromSelection,
  selectionFromCoverage,
} from '../engine/maskOps';
import { activeDocument, commitHistory, commitMeta, setActiveLayer } from './actions';
import { useAppStore, type AppState } from './store';

const engine = getEngine();

function state(): AppState {
  return useAppStore.getState();
}

function withChannels(doc: DocumentMeta, channels: AlphaChannel[]): DocumentMeta {
  return { ...doc, channels };
}

/** Drops a channel selection that pointed at a mask which no longer exists. */
function forgetMaskChannel(layerId: string): void {
  const active = state().activeChannel;
  if (active.kind === 'mask' && active.layerId === layerId) {
    state().setActiveChannel({ kind: 'composite' });
  }
  if (state().maskView !== 'off') state().setMaskView('off');
}

// --- Layer masks ---

/**
 * Layer - Raster Mask - Add. With a live selection the mask starts as that
 * selection, which is how masks are usually made; `hide` inverts it, matching
 * Photoshop's "Hide Selection".
 */
export function addLayerMask(layerId: string, hide = false): void {
  const doc = activeDocument();
  if (!doc) return;
  const layer = findLayerById(doc.layers, layerId);
  if (!layer || layer.hasMask) return;

  const resources = engine.resourcesFor(doc);
  const ops: EngineHistoryOp[] =
    maskFromSelection(engine, doc, layerId, hide) ??
    (() => {
      resources.ensureMask(layerId);
      return [layerMaskOp(layerId, null, blankSnapshot(doc.width, doc.height, [1, 1, 1, 1]))];
    })();

  const next: DocumentMeta = {
    ...doc,
    layers: mapLayer(doc.layers, layerId, (target) =>
      applyLayerPatch(target, { hasMask: true, maskEnabled: true }),
    ),
  };
  commitMeta(doc, 'Add Layer Mask', next, ops);
}

export function removeLayerMask(layerId: string): void {
  const doc = activeDocument();
  if (!doc) return;
  const before = engine.snapshotLayerMask(doc, layerId);
  engine.peek(doc.id)?.removeMask(layerId);
  const next: DocumentMeta = {
    ...doc,
    layers: mapLayer(doc.layers, layerId, (layer) => applyLayerPatch(layer, { hasMask: false })),
  };
  forgetMaskChannel(layerId);
  commitMeta(doc, 'Delete Layer Mask', next, [layerMaskOp(layerId, before, null)]);
}

export function toggleLayerMaskEnabled(layerId: string): void {
  const doc = activeDocument();
  if (!doc) return;
  const layer = findLayerById(doc.layers, layerId);
  if (!layer?.hasMask) return;
  const next: DocumentMeta = {
    ...doc,
    layers: mapLayer(doc.layers, layerId, (target) =>
      applyLayerPatch(target, { maskEnabled: !layer.maskEnabled }),
    ),
  };
  commitMeta(doc, layer.maskEnabled ? 'Disable Layer Mask' : 'Enable Layer Mask', next);
}

/** An unlinked mask stays put while the Move tool drags the layer's pixels. */
export function toggleLayerMaskLinked(layerId: string): void {
  const doc = activeDocument();
  if (!doc) return;
  const layer = findLayerById(doc.layers, layerId);
  if (!layer?.hasMask) return;
  const next: DocumentMeta = {
    ...doc,
    layers: mapLayer(doc.layers, layerId, (target) =>
      applyLayerPatch(target, { maskLinked: !layer.maskLinked }),
    ),
  };
  commitMeta(doc, layer.maskLinked ? 'Unlink Layer Mask' : 'Link Layer Mask', next);
}

/** Blocks painting on the mask until unlocked. */
export function toggleLayerMaskLocked(layerId: string): void {
  const doc = activeDocument();
  if (!doc) return;
  const layer = findLayerById(doc.layers, layerId);
  if (!layer?.hasMask) return;
  const next: DocumentMeta = {
    ...doc,
    layers: mapLayer(doc.layers, layerId, (target) =>
      applyLayerPatch(target, { lockMask: !layer.lockMask }),
    ),
  };
  commitMeta(doc, layer.lockMask ? 'Unlock Layer Mask' : 'Lock Layer Mask', next);
}

export function invertLayerMask(layerId: string): void {
  const doc = activeDocument();
  if (!doc) return;
  commitHistory(doc.id, 'Invert Layer Mask', invertLayerMaskPixels(engine, doc, layerId));
}

export function applyLayerMask(layerId: string): void {
  const doc = activeDocument();
  if (!doc) return;
  const layer = findLayerById(doc.layers, layerId);
  // A group has no pixels of its own, so its mask can only be deleted, not baked.
  if (!layer || !isSurfaceLayer(layer)) {
    state().setStatus('Only a pixel layer can have its mask applied');
    return;
  }
  const ops = applyLayerMaskPixels(engine, doc, layerId);
  if (!ops) return;
  const next: DocumentMeta = {
    ...doc,
    layers: mapLayer(doc.layers, layerId, (layer) => applyLayerPatch(layer, { hasMask: false })),
  };
  forgetMaskChannel(layerId);
  commitMeta(doc, 'Apply Layer Mask', next, ops);
}

/** Clicking a mask thumbnail moves editing onto the mask, as Photoshop does. */
export function selectMaskTarget(layerId: string): void {
  setActiveLayer(layerId);
  state().setPaintTarget('mask');
  state().setActiveChannel({ kind: 'mask', layerId });
  engine.invalidate();
}

export function selectLayerTarget(layerId: string): void {
  setActiveLayer(layerId);
  state().setPaintTarget('layer');
  setActiveChannel({ kind: 'composite' });
}

/**
 * Alt+click on a mask looks at the mask itself; adding Shift shows it as a red
 * overlay over the image instead, which is how Photoshop separates the two.
 */
export function toggleMaskView(layerId: string, mode: Exclude<MaskViewMode, 'off'> = 'grey'): void {
  const current = state();
  if (current.maskView === mode) {
    current.setMaskView('off');
  } else {
    selectMaskTarget(layerId);
    current.setMaskView(mode);
  }
  engine.invalidate();
}

export function selectionFromLayerMask(layerId: string): void {
  const doc = activeDocument();
  if (!doc) return;
  const mask = engine.resourcesFor(doc).layer(layerId)?.mask;
  const ops = selectionFromCoverage(engine, doc, mask);
  if (!ops) return;
  syncSelection(doc.id);
  commitHistory(doc.id, 'Mask to Selection', ops);
}

// --- Channels ---

function syncSelection(docId: string): void {
  const resources = engine.peek(docId);
  if (!resources) return;
  const hasSelection = !resources.selection.isEmpty;
  state().replaceDocument(docId, (doc) =>
    doc.hasSelection === hasSelection ? doc : { ...doc, hasSelection },
  );
}

export function setColorChannelVisible(index: 0 | 1 | 2, visible: boolean): void {
  const doc = activeDocument();
  if (!doc) return;
  const key = index === 0 ? 'r' : index === 1 ? 'g' : 'b';
  state().setDocument({ ...doc, colorVisible: { ...doc.colorVisible, [key]: visible } });
  engine.invalidate();
}

/** Clicking a colour channel row shows that channel alone, as Photoshop does. */
export function showColorChannelAlone(index: 0 | 1 | 2 | null): void {
  const doc = activeDocument();
  if (!doc) return;
  const colorVisible =
    index === null
      ? { r: true, g: true, b: true }
      : { r: index === 0, g: index === 1, b: index === 2 };
  state().setDocument({ ...doc, colorVisible });
  engine.invalidate();
}

export function toggleChannelVisible(channelId: string): void {
  const doc = activeDocument();
  if (!doc) return;
  const channels = doc.channels.map((channel) =>
    channel.id === channelId ? { ...channel, visible: !channel.visible } : channel,
  );
  state().setDocument(withChannels(doc, channels));
  engine.invalidate();
}

export function addChannel(fromSelection = false): void {
  const doc = activeDocument();
  if (!doc) return;
  const channel = createAlphaChannel(nextChannelName(doc.channels));
  const next = withChannels(doc, [...doc.channels, channel]);

  let ops: EngineHistoryOp[];
  if (fromSelection) {
    ops = channelFromSelection(engine, doc, channel.id);
  } else {
    engine.resourcesFor(doc).ensureChannel(channel.id);
    ops = [channelOp(channel.id, null, blankSnapshot(doc.width, doc.height, [0, 0, 0, 0]))];
  }

  commitMeta(doc, fromSelection ? 'Selection to Channel' : 'New Channel', next, ops);
  setActiveChannel({ kind: 'alpha', id: channel.id });
}

export function deleteChannel(channelId: string): void {
  const doc = activeDocument();
  if (!doc) return;
  const before = engine.snapshotChannel(doc, channelId);
  engine.peek(doc.id)?.removeChannel(channelId);
  const next = withChannels(
    doc,
    doc.channels.filter((channel) => channel.id !== channelId),
  );
  const active = state().activeChannel;
  if (active.kind === 'alpha' && active.id === channelId) {
    state().setActiveChannel({ kind: 'composite' });
  }
  if (channelId === QUICK_MASK_ID) state().setQuickMask(false);
  commitMeta(doc, 'Delete Channel', next, [channelOp(channelId, before, null)]);
}

export function duplicateChannel(channelId: string): void {
  const doc = activeDocument();
  if (!doc) return;
  const source = findChannel(doc, channelId);
  const sourceTarget = engine.resourcesFor(doc).channel(channelId);
  if (!source || !sourceTarget) return;

  const copy = createAlphaChannel(nextChannelName(doc.channels, `${source.name} copy`));
  const resources = engine.resourcesFor(doc);
  const target = resources.ensureChannel(copy.id);
  engine.renderer.copy(target.framebuffer, sourceTarget.texture);

  const next = withChannels(doc, [...doc.channels, copy]);
  commitMeta(doc, 'Duplicate Channel', next, [
    channelOp(copy.id, null, engine.snapshotChannel(next, copy.id)),
  ]);
}

export function renameChannel(channelId: string, name: string): void {
  const doc = activeDocument();
  if (!doc) return;
  const trimmed = name.trim();
  if (!trimmed) return;
  const channels = doc.channels.map((channel) =>
    channel.id === channelId ? { ...channel, name: trimmed } : channel,
  );
  commitMeta(doc, 'Rename Channel', withChannels(doc, channels));
}

export function selectionToChannel(): void {
  const doc = activeDocument();
  if (!doc) return;
  if (!doc.hasSelection) {
    state().setStatus('Select an area first');
    return;
  }
  addChannel(true);
}

export function channelToSelection(channelId: string): void {
  const doc = activeDocument();
  if (!doc) return;
  const channel = engine.resourcesFor(doc).channel(channelId);
  const ops = selectionFromCoverage(engine, doc, channel);
  if (!ops) return;
  syncSelection(doc.id);
  commitHistory(doc.id, 'Channel to Selection', ops);
}

export function setActiveChannel(ref: ChannelRef): void {
  state().setActiveChannel(ref);
  // The options bar and the panel must agree on what a stroke edits.
  state().setPaintTarget(ref.kind === 'mask' ? 'mask' : 'layer');
  if (ref.kind !== 'mask' && state().maskView === 'grey') state().setMaskView('off');

  // Editing one channel means looking at it, so the view follows the selection.
  if (ref.kind === 'color') showColorChannelAlone(ref.index);
  else if (ref.kind === 'composite') showColorChannelAlone(null);
  engine.invalidate();
}

/**
 * Quick Mask is a real channel in Photopea: entering the mode turns the
 * selection into a channel shown as a red overlay, and leaving it turns the
 * channel back into a selection.
 */
export function toggleQuickMask(): void {
  const doc = activeDocument();
  if (!doc) return;
  const resources = engine.resourcesFor(doc);
  const existing = quickMaskChannel(doc);

  if (existing) {
    // Leaving the mode turns the painted channel back into the selection.
    const ops = selectionFromCoverage(engine, doc, resources.channel(existing.id)) ?? [];
    ops.push(channelOp(existing.id, engine.snapshotChannel(doc, existing.id), null));
    resources.removeChannel(existing.id);

    const next = withChannels(
      doc,
      doc.channels.filter((channel) => channel.id !== existing.id),
    );
    state().setQuickMask(false);
    state().setActiveChannel({ kind: 'composite' });
    state().setStatus('Quick Mask off');
    commitMeta(doc, 'Quick Mask Off', next, ops);
    syncSelection(doc.id);
    return;
  }

  const channel = createQuickMaskChannel();
  const ops = channelFromSelection(engine, doc, channel.id);

  // The selection lives in the channel while the mode is on, so the ants go away.
  const selectionBefore = engine.snapshotSelection(doc);
  resources.selection.deselect();
  ops.push(selectionOp(selectionBefore, null));

  const next = withChannels({ ...doc, hasSelection: false }, [...doc.channels, channel]);
  state().setQuickMask(true);
  state().setActiveChannel({ kind: 'alpha', id: channel.id });
  state().setStatus('Quick Mask on: paint black to protect, white to select');
  commitMeta(doc, 'Quick Mask On', next, ops);
}
